import * as React from "react";
import { useState, useCallback } from "react";
import { Copy, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * CopyButton - A reusable copy-to-clipboard component
 * 
 * @param {string} value - The text to copy
 * @param {string} label - Accessibility label (e.g., "Copy patient phone number")
 * @param {string} toastMessage - Message shown after copy (e.g., "Phone number copied")
 * @param {string} variant - Display variant: "icon" | "inline" | "button"
 * @param {string} size - Size: "xs" | "sm" | "md"
 * @param {boolean} showOnHover - Only show on parent hover (for dense UIs)
 * @param {string} className - Additional classes
 */
export function CopyButton({
  value,
  label = "Copy to clipboard",
  toastMessage = "Copied to clipboard",
  variant = "icon",
  size = "sm",
  showOnHover = false,
  className,
  children,
}) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(false);

  const handleCopy = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!value) {
      toast.error("Nothing to copy");
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setError(false);
      toast.success(toastMessage, {
        duration: 2000,
        position: "bottom-center",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError(true);
      toast.error("Could not copy", {
        duration: 2000,
        position: "bottom-center",
      });
      setTimeout(() => setError(false), 2000);
    }
  }, [value, toastMessage]);

  const sizeClasses = {
    xs: "w-3 h-3",
    sm: "w-3.5 h-3.5",
    md: "w-4 h-4",
  };

  const buttonSizeClasses = {
    xs: "p-0.5",
    sm: "p-1",
    md: "p-1.5",
  };

  const Icon = copied ? Check : error ? AlertCircle : Copy;
  const iconColor = copied ? "text-green-600" : error ? "text-red-500" : "text-[#757675]";

  // Icon-only variant (most common)
  if (variant === "icon") {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleCopy}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleCopy(e);
                }
              }}
              aria-label={label}
              className={cn(
                "inline-flex items-center justify-center rounded-md transition-all duration-150",
                "hover:bg-[#E8E9E8] focus:outline-none focus:ring-2 focus:ring-[#205B5A]/30 focus:ring-offset-1",
                "disabled:pointer-events-none disabled:opacity-50",
                buttonSizeClasses[size],
                showOnHover && "opacity-0 group-hover:opacity-100",
                className
              )}
              data-testid="copy-button"
            >
              <Icon className={cn(sizeClasses[size], iconColor, "transition-colors")} />
            </button>
          </TooltipTrigger>
          <TooltipContent 
            side="top" 
            className="bg-[#1A1B1A] text-white text-[10px] px-2 py-1"
          >
            {copied ? "Copied!" : error ? "Failed" : "Copy"}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Inline variant (text + icon)
  if (variant === "inline") {
    return (
      <span className={cn("inline-flex items-center gap-1.5 group", className)}>
        {children}
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleCopy}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleCopy(e);
                  }
                }}
                aria-label={label}
                className={cn(
                  "inline-flex items-center justify-center rounded-md transition-all duration-150",
                  "hover:bg-[#E8E9E8] focus:outline-none focus:ring-2 focus:ring-[#205B5A]/30 focus:ring-offset-1",
                  buttonSizeClasses[size],
                  showOnHover && "opacity-0 group-hover:opacity-100"
                )}
                data-testid="copy-button-inline"
              >
                <Icon className={cn(sizeClasses[size], iconColor, "transition-colors")} />
              </button>
            </TooltipTrigger>
            <TooltipContent 
              side="top" 
              className="bg-[#1A1B1A] text-white text-[10px] px-2 py-1"
            >
              {copied ? "Copied!" : error ? "Failed" : "Copy"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </span>
    );
  }

  // Button variant (full button with text)
  if (variant === "button") {
    return (
      <button
        type="button"
        onClick={handleCopy}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleCopy(e);
          }
        }}
        aria-label={label}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full transition-all duration-150",
          "text-[10px] font-medium px-3 py-1",
          "bg-[#E8E9E8]/50 hover:bg-[#E8E9E8] text-[#496275]",
          "focus:outline-none focus:ring-2 focus:ring-[#205B5A]/30 focus:ring-offset-1",
          className
        )}
        data-testid="copy-button-full"
      >
        <Icon className={cn(sizeClasses[size], iconColor, "transition-colors")} />
        <span>{copied ? "Copied" : children || "Copy"}</span>
      </button>
    );
  }

  return null;
}

/**
 * CopyableField - A field value with integrated copy button
 * 
 * @param {string} value - The text to display and copy
 * @param {string} label - Field label (e.g., "Phone")
 * @param {string} copyLabel - Accessibility label for copy button
 * @param {string} toastMessage - Message shown after copy
 * @param {boolean} showLabel - Whether to show the label
 * @param {string} className - Additional classes
 */
export function CopyableField({
  value,
  label,
  copyLabel,
  toastMessage,
  showLabel = false,
  truncate = false,
  className,
}) {
  if (!value) return null;

  return (
    <div className={cn("group flex items-center gap-1", className)}>
      {showLabel && label && (
        <span className="text-[10px] text-[#757675] mr-1">{label}:</span>
      )}
      <span className={cn(
        "text-[11px] text-[#4A4B4A]",
        truncate && "truncate max-w-[180px]"
      )}>
        {value}
      </span>
      <CopyButton
        value={value}
        label={copyLabel || `Copy ${label || "value"}`}
        toastMessage={toastMessage || `${label || "Value"} copied`}
        size="xs"
        showOnHover
      />
    </div>
  );
}

/**
 * CopyableText - Minimal inline copyable text
 * Used for IDs, references, codes where space is tight
 */
export function CopyableText({
  value,
  label,
  toastMessage,
  mono = false,
  className,
}) {
  if (!value) return null;

  return (
    <span className={cn("inline-flex items-center gap-1 group", className)}>
      <span className={cn(
        "text-[9px]",
        mono ? "font-mono text-[#496275]" : "text-[#4A4B4A]"
      )}>
        {value}
      </span>
      <CopyButton
        value={value}
        label={label || "Copy"}
        toastMessage={toastMessage || "Copied"}
        size="xs"
        showOnHover
      />
    </span>
  );
}

export default CopyButton;
